import {
  Avatar,
  BrandMark,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@artisan/ui";
import { CloudUpload, House, LogOut, type LucideIcon, Store, WifiOff } from "lucide-react";
import { NavLink, Outlet } from "react-router";

import { activeOrg, logout, switchOrg } from "../lib/auth";
import { useSession } from "../lib/hooks";
import { useOutboxStatus } from "../outbox/outbox";

function RailLink({ to, icon: Icon, label, badge }: { to: string; icon: LucideIcon; label: string; badge?: number }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        cn(
          "relative flex w-[72px] flex-col items-center gap-1 rounded-xl px-1 py-2.5 text-[11px] font-medium transition-colors",
          isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : "text-sidebar-foreground hover:bg-sidebar-accent/60",
        )
      }
    >
      <Icon className="size-[22px]" aria-hidden strokeWidth={1.6} />
      {label}
      {badge ? (
        <span className="absolute top-1.5 right-3 grid min-w-[18px] place-items-center rounded-full bg-primary px-1 text-[10px] leading-[18px] text-primary-foreground tabular">
          {badge}
        </span>
      ) : null}
    </NavLink>
  );
}

export function Shell() {
  const session = useSession();
  const outbox = useOutboxStatus();
  const org = activeOrg(session);
  return (
    <div className="flex h-full">
      <nav aria-label="Үндсэн цэс" className="flex w-[92px] shrink-0 flex-col items-center gap-2 bg-sidebar py-4 text-sidebar-primary">
        <BrandMark size={36} className="mb-4" />
        <RailLink to="/" icon={House} label="Нүүр" />
        <RailLink to="/catalog" icon={Store} label="Каталог" />
        <RailLink to="/outbox" icon={CloudUpload} label="Илгээх" badge={outbox.pending} />
        <div className="mt-auto flex flex-col items-center gap-3">
          {!outbox.online ? (
            <span className="flex flex-col items-center gap-1 text-[10px] text-sidebar-foreground" role="status">
              <WifiOff className="size-5" aria-hidden />
              Офлайн
            </span>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger aria-label="Бүртгэл" className="rounded-full outline-none focus-visible:shadow-ring">
              <Avatar name={session.user.display_name || session.user.email} />
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end" className="w-64">
              <DropdownMenuLabel>
                <div className="font-medium">{session.user.display_name}</div>
                <div className="text-xs font-normal text-muted-foreground">{session.user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {session.orgs.length > 1 ? (
                <DropdownMenuRadioGroup value={org?.id ?? ""} onValueChange={(id) => void switchOrg(id)}>
                  {session.orgs.map((o) => (
                    <DropdownMenuRadioItem key={o.id} value={o.id}>
                      {o.name}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              ) : (
                <DropdownMenuLabel className="font-normal text-muted-foreground">{org?.name ?? "Байгууллагагүй"}</DropdownMenuLabel>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => void logout()}>
                <LogOut aria-hidden /> Гарах
              </DropdownMenuItem>
              {outbox.pending ? (
                <p className="px-2 pb-2 text-xs text-muted-foreground">
                  {outbox.pending} илгээгдээгүй бичлэг төхөөрөмж дээр хадгалагдсан; дахин нэвтрэхэд илгээгдэнэ.
                </p>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
      <main className="min-w-0 flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
